import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { axiosInstance } from "../../utils/axios";
import { sdkConfig } from "../../config";
import {
  Button,
  Paper,
  Checkbox,
  Divider as MuiDivider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  MenuItem,
  DialogActions,
  TextField as MuiTextField,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormControl,
  FormGroup,
  FormLabel,
  RadioGroup,
  Radio,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  CircularProgress,
  Box,
} from "@mui/material";
import {
  Add as AddIcon,
  Send as SendIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { spacing } from "@mui/system";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

const Divider = styled(MuiDivider)(spacing);
const PaperStyled = styled(Paper)(spacing);

const headCells = [
  { id: "name", alignment: "left", label: "Name" },
  { id: "email", alignment: "left", label: "Email" },
  { id: "jobTitle", alignment: "left", label: "Job Title" },
  { id: "organization", alignment: "left", label: "Organization" },
  { id: "category", alignment: "left", label: "Category" },
  { id: "city", alignment: "left", label: "City" },
  { id: "country", alignment: "left", label: "Country" },
  { id: "inviteStatus", alignment: "center", label: "Invite Status" },
  { id: "actions", alignment: "center", label: "Actions" },
];

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  jobTitle: Yup.string().required("Job Title is required"),
  organization: Yup.string().required("Organization is required"),
  city: Yup.string().required("City is required"),
  country: Yup.string().required("Country is required"),
});

// List of countries for dropdown
const countries = [
  "Australia",
  "Brazil",
  "Canada",
  "China",
  "France",
  "Germany",
  "India",
  "Indonesia",
  "Italy",
  "Japan",
  "Mexico",
  "Russia",
  "Saudi Arabia",
  "South Africa",
  "South Korea",
  "Spain",
  "Turkey",
  "United Kingdom",
  "United States",
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => ({
    el,
    index,
  }));
  stabilizedThis.sort((a, b) => {
    const order = comparator(a.el, b.el);
    if (order !== 0) return order;
    return a.index - b.index;
  });
  return stabilizedThis.map((element) => element.el);
}

const EnhancedTableHead = (props) => {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.alignment}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const getInviteStatusChip = (row) => {
  if (row.lastLogin) {
    return <Chip label="Joined" size="small" color="success" />;
  }
  if (row.accessToken) {
    return <Chip label="Invited" size="small" color="warning" />;
  }
  return <Chip label="No Invite" size="small" variant="outlined" />;
};

const EnhancedTable = ({ rows, setRows, onResendInvite, onCopyLink, sendingId }) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  return (
    <PaperStyled>
      <TableContainer>
        <Table>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {stableSort(rows, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <TableRow key={row.id || index}>
                  <TableCell>{`${row.firstName} ${row.lastName}`}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.jobTitle}</TableCell>
                  <TableCell>{row.organization}</TableCell>
                  <TableCell>{row.category}</TableCell>
                  <TableCell>{row.city}</TableCell>
                  <TableCell>{row.country}</TableCell>
                  <TableCell align="center">
                    {getInviteStatusChip(row)}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                      <Tooltip title={row.accessToken ? "Resend Invite" : "Send Invite"}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onResendInvite(row)}
                            disabled={sendingId === row.id}
                          >
                            {sendingId === row.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <SendIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      {row.accessToken && (
                        <Tooltip title="Copy Invite Link">
                          <IconButton
                            size="small"
                            onClick={() => onCopyLink(row)}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={9} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </PaperStyled>
  );
};

function People({ selectedType, eventId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchAttendees = async () => {
    if (!selectedType?.id) return;

    try {
      setLoading(true);
      console.log("Fetching attendees for category:", selectedType.id);

      const query = `
        query GetEventAttendees($eventId: ID!, $category: String) {
          getEventAttendees(eventId: $eventId, category: $category) {
            id
            firstName
            lastName
            email
            jobTitle
            organization
            city
            country
            addedVia
            category
            accessToken
            lastLogin
          }
        }
      `;

      const response = await axiosInstance.post("/graphql", {
        query,
        variables: {
          eventId,
          category: selectedType.id,
        },
      });

      console.log("Attendees response:", response);

      if (response.errors) {
        throw new Error(
          response.errors[0].message || "Error fetching attendees"
        );
      }

      // Correct the path to get attendees from the response
      const attendees = response.data?.getEventAttendees || [];
      console.log("Fetched attendees:", attendees);

      setRows(attendees);
    } catch (error) {
      console.error("Error fetching attendees:", error);
      setError(error.message || "Failed to fetch attendees");
      setAlert({
        open: true,
        message: error.message || "Failed to fetch attendees",
        severity: "error",
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch attendees when the selected category changes
  useEffect(() => {
    fetchAttendees();
  }, [selectedType, eventId]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      console.log("Submitting new attendee:", values);
      console.log("Using category:", selectedType.id);

      // Prepare the attendee data with the selected category
      const newAttendee = {
        eventId,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        jobTitle: values.jobTitle,
        organization: values.organization,
        city: values.city,
        country: values.country,
        addedVia: "Invite",
        sendEmail: true,
        category: selectedType.id, // Use the category from selectedType
      };

      const response = await axiosInstance.post("/graphql", {
        query: `
        mutation CreateAttendee($input: CreateAttendeeInput!) {
          createAttendee(input: $input) {
            id
            firstName
            lastName
            email
            jobTitle
            organization
            city
            country
            addedVia
            category
          }
        }
      `,
        variables: {
          input: newAttendee,
        },
      });

      console.log("Create attendee response:", response);

      if (response.errors) {
        throw new Error(
          response.errors[0].message || "Error creating attendee"
        );
      }

      // Use the correct path to get the created attendee
      const createdAttendee = response.data?.createAttendee;

      if (createdAttendee) {
        console.log("Successfully created attendee:", createdAttendee);

        // Add the new attendee to the rows
        setRows((prevRows) => [...prevRows, createdAttendee]);

        setAlert({
          open: true,
          message: "Attendee added successfully",
          severity: "success",
        });

        resetForm();
        setOpen(false);

        // Refresh the attendees list
        fetchAttendees();
      } else {
        throw new Error("Failed to create attendee");
      }
    } catch (error) {
      console.error("Error creating attendee:", error);
      setAlert({
        open: true,
        message: error.message || "Failed to create attendee",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvite = async (row) => {
    try {
      setSendingId(row.id);
      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation ResendInvitation($eventId: ID!, $attendeeId: ID!) {
            resendInvitation(eventId: $eventId, attendeeId: $attendeeId)
          }
        `,
        variables: { eventId, attendeeId: row.id },
      });

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      setAlert({
        open: true,
        message: `Invitation sent to ${row.email}`,
        severity: "success",
      });
      fetchAttendees();
    } catch (err) {
      setAlert({
        open: true,
        message: err.message || "Failed to send invitation",
        severity: "error",
      });
    } finally {
      setSendingId(null);
    }
  };

  const handleCopyLink = (row) => {
    if (!row.accessToken) return;
    const link = `${sdkConfig.sdkURI}/event.html?token=${row.accessToken}`;
    navigator.clipboard.writeText(link).then(() => {
      setAlert({
        open: true,
        message: "Invite link copied to clipboard",
        severity: "success",
      });
    }).catch(() => {
      setAlert({
        open: true,
        message: "Failed to copy link",
        severity: "error",
      });
    });
  };

  if (!selectedType) {
    return (
      <Typography variant="body1" align="center" sx={{ py: 3 }}>
        Please select a category
      </Typography>
    );
  }

  return (
    <React.Fragment>
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      <Grid container justifyContent="space-between" alignItems="center" mb={3}>
        <Grid item>
          <Typography variant="h5" gutterBottom>
            Managing {selectedType?.name || "Attendees"}
          </Typography>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpen(true)}
            startIcon={<AddIcon />}
          >
            Add Attendee
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : rows.length > 0 ? (
        <EnhancedTable
          rows={rows}
          setRows={setRows}
          onResendInvite={handleResendInvite}
          onCopyLink={handleCopyLink}
          sendingId={sendingId}
        />
      ) : (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" color="textSecondary">
            No attendees found in this category. Click "Add Attendee" to create
            one.
          </Typography>
        </Paper>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="form-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">
          Add New {selectedType?.name || "Attendee"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Add attendee to {selectedType?.name || "this category"}. Please fill
            all mandatory form fields.
          </DialogContentText>
          <br />
          <Formik
            initialValues={{
              firstName: "",
              lastName: "",
              email: "",
              jobTitle: "",
              organization: "",
              city: "",
              country: "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values, handleChange, touched, errors }) => (
              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={MuiTextField}
                      name="firstName"
                      label="First Name"
                      variant="outlined"
                      fullWidth
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={MuiTextField}
                      name="lastName"
                      label="Last Name"
                      variant="outlined"
                      fullWidth
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={MuiTextField}
                      name="email"
                      label="Email"
                      variant="outlined"
                      fullWidth
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={MuiTextField}
                      name="jobTitle"
                      label="Job Title"
                      variant="outlined"
                      fullWidth
                      error={touched.jobTitle && Boolean(errors.jobTitle)}
                      helperText={touched.jobTitle && errors.jobTitle}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={MuiTextField}
                      name="organization"
                      label="Organization"
                      variant="outlined"
                      fullWidth
                      error={
                        touched.organization && Boolean(errors.organization)
                      }
                      helperText={touched.organization && errors.organization}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={MuiTextField}
                      name="city"
                      label="City"
                      variant="outlined"
                      fullWidth
                      error={touched.city && Boolean(errors.city)}
                      helperText={touched.city && errors.city}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      error={touched.country && Boolean(errors.country)}
                    >
                      <InputLabel id="country-label">Country</InputLabel>
                      <Field
                        as={Select}
                        labelId="country-label"
                        id="country"
                        name="country"
                        label="Country"
                        value={values.country}
                        onChange={handleChange}
                      >
                        {countries.map((country) => (
                          <MenuItem key={country} value={country}>
                            {country}
                          </MenuItem>
                        ))}
                      </Field>
                      {touched.country && errors.country && (
                        <Typography variant="caption" color="error">
                          {errors.country}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
                <DialogActions sx={{ mt: 3 }}>
                  <Button onClick={() => setOpen(false)} color="inherit">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    color="primary"
                    variant="contained"
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} />
                    ) : (
                      `Add ${selectedType?.name || "Attendee"}`
                    )}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}

export default People;
